import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate, fmtDateShort, cycleShort } from "@/lib/fmt";
import { Check, Wand2, Loader2, X, Mail, Download } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/admin/payments");
    setPayments(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/payments/generate");
      toast.success(`Generated ${data.generated} statements (skipped ${data.skipped_existing} existing).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setGenerating(false); }
  };

  const copyCombinedForAuthor = async (cycle, authorPayments) => {
    const text = buildCombinedEmail(cycle, authorPayments);
    const ok = await copyToClipboard(text);
    if (ok) toast.success("Combined statement copied to clipboard");
    else toast.error("Could not access clipboard — please copy manually");
  };

  // Memoized so a re-render doesn't re-sort the whole table on every state change.
  const grouped = useMemo(() => {
    return payments.reduce((acc, p) => {
      (acc[p.cycle] ||= []).push(p);
      return acc;
    }, {});
  }, [payments]);

  const cyclesDesc = useMemo(
    () => Object.keys(grouped).sort().reverse(),
    [grouped]
  );

  const groupedByAuthor = useMemo(() => {
    // {cycle: [[authorId, {author_name, rows}], ...sorted]}
    const out = {};
    for (const cycle of cyclesDesc) {
      const m = {};
      for (const p of grouped[cycle]) {
        (m[p.author_id] ||= { author_name: p.author_name, rows: [] }).rows.push(p);
      }
      out[cycle] = Object.entries(m).sort((a, b) => a[1].author_name.localeCompare(b[1].author_name));
    }
    return out;
  }, [grouped, cyclesDesc]);

  const onDownloadBackup = async () => {
    try {
      const resp = await api.get("/admin/payments.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const now = new Date().toISOString().slice(0, 16).replace(":", "");
      a.download = `payments_snapshot_${now}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payments snapshot downloaded");
    } catch (e) {
      toast.error("Couldn't download snapshot");
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-payments-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="hh-overline mb-3">Admin · Payments</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Author payments & statements</h1>
          <p className="mt-3 text-hh-ink2 max-w-2xl">
            One row per author per cycle per currency. Click <em>Mark as paid</em> when you actually send a payment —
            authors see the &ldquo;paid&rdquo; status and date on their Statements page in real time.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onDownloadBackup} className="hh-btn-outline" data-testid="download-payments-backup-btn" title="Download a CSV snapshot of every payment row (paid + pending). Useful as a safety backup before any deploy or sync.">
            <Download size={14} strokeWidth={1.8} className="inline mr-2" /> Download backup CSV
          </button>
          <button onClick={onGenerate} disabled={generating} className="hh-btn-primary disabled:opacity-50" data-testid="generate-statements-btn">
            {generating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wand2 size={16} className="mr-2" />}
            {generating ? "Generating…" : "Generate from sales data"}
          </button>
        </div>
      </header>

      <p className="text-xs text-hh-ink2 bg-hh-muted/60 border border-hh-border rounded-lg px-4 py-3 inline-block">
        <strong className="text-hh-ink">How this works:</strong> the &ldquo;Generate&rdquo; button creates one pending statement per author/currency for every <em>closed</em> quarterly cycle (end date already passed) — based on the sales data you&rsquo;ve uploaded. Existing rows are never overwritten.
      </p>

      {loading ? (
        <div className="text-hh-ink2">Loading payments…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="hh-card p-12 text-center">
          <p className="font-serif text-2xl text-hh-ink">No payments recorded yet</p>
          <p className="text-sm text-hh-ink2 mt-2 max-w-md mx-auto">
            Upload your historical IngramSpark PDFs first (<a className="text-hh-primary hover:underline" href="/admin/upload">Upload Reports</a>), then click <strong>Generate from sales data</strong> to create one pending statement per author per closed cycle.
          </p>
        </div>
      ) : (
        cyclesDesc.map((cycle) => (
          <section key={cycle} data-testid={`payments-cycle-${cycle}`} className="space-y-5">
            <h2 className="font-serif text-2xl text-hh-ink">
              {cycleShort(cycle)}
            </h2>

            {/* Combined statement per author — multi-currency, one mail icon */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`combined-${cycle}`}>
              {groupedByAuthor[cycle].map(([authorId, group]) => (
                <CombinedAuthorCard
                  key={authorId}
                  cycle={cycle}
                  authorName={group.author_name}
                  rows={group.rows}
                  onCopy={() => copyCombinedForAuthor(cycle, group.rows)}
                />
              ))}
            </div>

            {/* Detailed currency-level rows for "Mark as paid" tracking */}
            <details className="hh-card" data-testid={`detailed-${cycle}`}>
              <summary className="px-6 py-3 cursor-pointer text-xs uppercase tracking-widest text-hh-ink2 hover:text-hh-ink">
                Detailed breakdown · mark each currency as paid
              </summary>
              <table className="w-full text-sm border-t border-hh-border">
                <thead className="bg-hh-muted/60 border-b border-hh-border text-left">
                  <tr>
                    <th className="px-6 py-3 hh-overline">Author</th>
                    <th className="px-6 py-3 hh-overline">Source</th>
                    <th className="px-6 py-3 hh-overline">Period</th>
                    <th className="px-6 py-3 hh-overline">Amount</th>
                    <th className="px-6 py-3 hh-overline">Status</th>
                    <th className="px-6 py-3 hh-overline">Paid on</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[cycle].map((p) => (
                    <tr key={p.id} className="border-b border-hh-border last:border-0" data-testid={`payment-${p.id}`}>
                      <td className="px-6 py-4 font-serif text-hh-ink">{p.author_name}</td>
                      <td className="px-6 py-4"><KindBadge kind={p.kind} /></td>
                      <td className="px-6 py-4 text-hh-ink2 text-xs">{fmtDateShort(p.period_start)} → {fmtDateShort(p.period_end)}</td>
                      <td className="px-6 py-4 font-medium text-hh-ink">{fmtMoney(p.amount, p.currency)}</td>
                      <td className="px-6 py-4">
                        <StatusPill status={p.status} />
                      </td>
                      <td className="px-6 py-4 text-hh-ink2 text-xs">{p.paid_date ? fmtDate(p.paid_date) : "—"}</td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button onClick={() => setEditing(p)} className="text-xs text-hh-primary hover:underline" data-testid={`edit-payment-${p.id}`}>
                          {p.status === "paid" ? "Edit" : "Mark as paid"} →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </section>
        ))
      )}

      {editing && (
        <PaymentEditor payment={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = status === "paid"
    ? { bg: "bg-hh-sage/15", text: "text-hh-sage", label: "Paid" }
    : { bg: "bg-hh-accent/15", text: "text-[#9F7300]", label: "Pending" };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs ${cfg.bg} ${cfg.text}`}>
      {status === "paid" && <Check size={11} strokeWidth={2.5} />} {cfg.label}
    </span>
  );
}

function KindBadge({ kind }) {
  const isDirect = kind === "direct";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-medium ${
        isDirect
          ? "bg-[#B519DE]/12 text-[#7A1196] border border-[#B519DE]/30"
          : "bg-hh-primary/10 text-hh-primary border border-hh-primary/20"
      }`}
    >
      {isDirect ? "Direct" : "IngramSpark"}
    </span>
  );
}

function sym(ccy) {
  return ({ AUD: "A$", USD: "US$", CAD: "C$", GBP: "£", EUR: "€" })[ccy] || `${ccy} `;
}

function fmtPeriod(iso) {
  // "2025-07-01" -> "1 July 2025"
  const [y, m, d] = iso.split("-").map(Number);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${d} ${months[m - 1]} ${y}`;
}

function buildCombinedEmail(cycle, rows) {
  if (!rows.length) return "";
  const first = (rows[0].author_name || "").split(" ")[0] || "there";
  const startStr = fmtPeriod(rows[0].period_start);
  const endStr = fmtPeriod(rows[0].period_end);

  // Aggregate IngramSpark vs Direct by currency
  const ingram = {};
  const direct = { totalUnits: 0, byCcy: {}, weighted: {} };
  rows.forEach((r) => {
    if (r.kind === "direct") {
      direct.totalUnits += r.units || 0;
      direct.byCcy[r.currency] = (direct.byCcy[r.currency] || 0) + Number(r.amount);
      direct.weighted[r.currency] = (direct.weighted[r.currency] || 0) + (r.royalty_per_book || 0) * (r.units || 0);
    } else {
      ingram[r.currency] = (ingram[r.currency] || 0) + Number(r.amount);
    }
  });

  const fmtLine = (ccy, amt) => `  ${sym(ccy)}${Number(amt).toFixed(2)} ${ccy}`;
  const ingramLines = Object.entries(ingram).sort().map(([c, v]) => fmtLine(c, v)).join("\n");
  const directLines = Object.entries(direct.byCcy).sort().map(([c, v]) => fmtLine(c, v)).join("\n");

  let body = `Dear ${first},

I'm delighted to share your royalty statement for the period ${startStr} - ${endStr}.

Period Covered: ${startStr} - ${endStr}
`;

  if (ingramLines) {
    body += `\nIngramSpark royalties:\n${ingramLines}\n`;
  }
  if (directLines) {
    const unitsLine = direct.totalUnits ? ` (${direct.totalUnits} book${direct.totalUnits === 1 ? "" : "s"} sold)` : "";
    body += `\nHealing House direct website sales${unitsLine}:\n${directLines}\n`;
  }

  body += `
This amount will be paid in AUD by EFT to your nominated bank account at the live exchange rate on the day of processing.

Please allow up to 5 business days for Australian bank accounts or 10 business days for international transfers to process. If the funds haven't arrived within this timeframe or if you have any questions, I'm always here to help — just reach out.

Warm regards,
Denzel`;

  return body;
}

function CombinedAuthorCard({ cycle, authorName, rows, onCopy }) {
  const ingram = rows.filter((r) => r.kind === "ingramspark");
  const direct = rows.filter((r) => r.kind === "direct");
  const ingramByCcy = {};
  ingram.forEach((r) => { ingramByCcy[r.currency] = (ingramByCcy[r.currency] || 0) + r.amount; });
  const directByCcy = {};
  let directUnits = 0;
  direct.forEach((r) => {
    directByCcy[r.currency] = (directByCcy[r.currency] || 0) + r.amount;
    directUnits += r.units || 0;
  });
  const allPaid = rows.every((r) => r.status === "paid");
  const someUnpaid = rows.some((r) => r.status !== "paid");
  return (
    <div className="hh-card p-5 flex flex-col gap-3" data-testid={`combined-card-${authorName.toLowerCase().replace(/\s+/g, "-")}-${cycle}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg text-hh-ink">{authorName}</h3>
          <p className="text-[11px] uppercase tracking-widest text-hh-ink2 mt-0.5">
            {cycleShort(cycle)}
          </p>
        </div>
        <button
          onClick={onCopy}
          className="hh-btn-ghost shrink-0"
          data-testid={`copy-combined-${authorName.toLowerCase().replace(/\s+/g, "-")}-${cycle}`}
        >
          <Mail size={13} className="mr-1.5" strokeWidth={1.5} /> Copy statement
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-2">
        {Object.keys(ingramByCcy).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-hh-primary mb-1">IngramSpark</p>
            <ul className="space-y-0.5">
              {Object.entries(ingramByCcy).sort().map(([ccy, amt]) => (
                <li key={ccy} className="text-sm text-hh-ink flex justify-between">
                  <span className="text-hh-ink2">{ccy}</span>
                  <span className="font-medium">{fmtMoney(amt, ccy)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {Object.keys(directByCcy).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#7A1196] mb-1">
              Direct {directUnits ? `· ${directUnits} book${directUnits === 1 ? "" : "s"}` : ""}
            </p>
            <ul className="space-y-0.5">
              {Object.entries(directByCcy).sort().map(([ccy, amt]) => (
                <li key={ccy} className="text-sm text-hh-ink flex justify-between">
                  <span className="text-hh-ink2">{ccy}</span>
                  <span className="font-medium">{fmtMoney(amt, ccy)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <p className="text-[11px] text-hh-ink2 border-t border-hh-border pt-2 mt-1">
        {allPaid ? "✓ All paid" : someUnpaid ? "● Pending — convert to AUD at the spot rate on the day of payment" : ""}
      </p>
    </div>
  );
}

function PaymentEditor({ payment, onClose, onSaved }) {
  const [status, setStatus] = useState(payment.status || "pending");
  const [paidDate, setPaidDate] = useState(payment.paid_date || new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(payment.amount);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/payments/${payment.id}`, {
        status,
        paid_date: status === "paid" ? paidDate : null,
        amount: parseFloat(amount) || payment.amount,
      });
      toast.success(status === "paid" ? "Marked as paid — author will see it immediately." : "Updated.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-hh-ink">{payment.author_name}</h2>
          <button onClick={onClose}><X size={18} className="text-hh-ink2" /></button>
        </div>
        <div className="mb-6 p-4 rounded-lg bg-hh-muted/60 border border-hh-border text-sm">
          <div className="text-hh-ink2">Cycle: <span className="text-hh-ink font-medium">{payment.cycle}</span></div>
          <div className="text-hh-ink2 mt-1">Period: <span className="text-hh-ink font-medium">{fmtDateShort(payment.period_start)} → {fmtDateShort(payment.period_end)}</span></div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-hh-ink2 mb-2">Amount ({payment.currency})</label>
            <input
              type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payment-amount"
              className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink"
            />
          </div>
          <div>
            <label className="block text-sm text-hh-ink2 mb-2">Status</label>
            <div className="flex gap-2">
              {["pending", "paid"].map((s) => (
                <button key={s} type="button"
                  onClick={() => setStatus(s)}
                  data-testid={`status-${s}`}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${
                    status === s ? "bg-hh-primary text-white border-hh-primary" : "bg-white text-hh-ink border-hh-border"
                  }`}
                >{s === "paid" ? "Paid" : "Pending"}</button>
              ))}
            </div>
          </div>
          {status === "paid" && (
            <div>
              <label className="block text-sm text-hh-ink2 mb-2">Paid on</label>
              <input
                type="date" value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                data-testid="payment-paid-date"
                className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink"
              />
            </div>
          )}
        </div>
        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onClose} className="hh-btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="hh-btn-primary disabled:opacity-50" data-testid="payment-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
